import { UNSYNCED_OUTBOX_STATES } from '../domain/outbox-item.js';
import type { OfflineDb } from '../infrastructure/dexie/offline-db.js';

export interface OutboxIdentity {
  orgId: string;
  capturedByUserId: string;
}

/**
 * getUnsyncedOutboxCount (task 6.8 — PendingSyncBadge) — everything the
 * CURRENT operator/org still needs synced or resolved: queued
 * (`pending`/`syncing`), or needing attention (`rejected`/`needs-auth`/
 * `held`). Deliberately wider than `getPendingOutboxCount` (sign-out-guard.ts,
 * task 3.7's own `pending`-only count) — that counter predates every state
 * but `pending` even existing in practice and is left as-is; this one is the
 * badge's own definition of "still my responsibility".
 *
 * `includeRejected: false` yields the "still to sync" count the sync/cloud
 * indicator shows: a `rejected` item is terminal (the server refused it and
 * waiting will never change that), so it must not keep the cloud badge lit —
 * it stays visible on /pending and in the Pending nav badge (default
 * `includeRejected: true`) until discarded or auto-purged.
 *
 * Scoped by `identity.orgId` ONLY (coordinator finding #4, adversarial
 * review — corrected by the SECOND adversarial review's MEDIUM finding #6,
 * design D12: items from ANY operator of the SAME ORG are visible/synced by
 * whichever same-org operator is logged in; `capturedByUserId` stays claimed
 * for audit only, never a visibility filter). An org switch (a DIFFERENT
 * org's items) must never show/count here — those stay `held`/`pending` in
 * Dexie, untouched.
 */
export function getUnsyncedOutboxCount(
  db: OfflineDb,
  identity: OutboxIdentity,
  options: { includeRejected?: boolean } = {},
): Promise<number> {
  const includeRejected = options.includeRejected ?? true;
  const states = includeRejected
    ? UNSYNCED_OUTBOX_STATES
    : UNSYNCED_OUTBOX_STATES.filter((state) => state !== 'rejected');
  return db.outbox
    .where('state')
    .anyOf(states)
    .and((row) => row.orgId === identity.orgId)
    .count();
}
